import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Plus, Sparkles } from "lucide-react";
import { PROFESSIONAL_CIRCLES } from "../../lib/site-data";
import SectionLabel from "./SectionLabel";

export default function ProfessionalCircles() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="circles"
      data-testid="circles-section"
      className="section-pad bg-white text-black"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10 md:mb-12"
        >
          <SectionLabel>Professional Circles</SectionLabel>
          <h2
            data-testid="circles-headline"
            className="font-display mt-7 text-xl md:text-2xl font-semibold tracking-tight leading-snug"
          >
            9 professional circles. Endless intersections.
          </h2>
          <div className="mt-5 space-y-4 max-w-[1000px]">
            <p
              className="text-base md:text-lg text-black/60 leading-relaxed"
              data-testid="circles-p1"
            >
              Each City Chapter includes these 9 industry-specific Professional Circles.
            </p>
            <p
              className="text-base md:text-lg text-black/60 leading-relaxed"
              data-testid="circles-p1b"
            >
              Each Professional Circle includes entrepreneurs, business owners, working
              professionals, consultants, freelancers, and creators who are
              actively involved in that field.
            </p>
            <p
              className="text-base md:text-lg text-black/60 leading-relaxed"
              data-testid="circles-p2"
            >
              When you apply for membership, you can select one Professional
              Circle as your Primary Circle based on your main field of work,
              and, where relevant, one Secondary Circle for another field you
              are actively involved in.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {PROFESSIONAL_CIRCLES.map((c, i) => {
            const Icon = Icons[c.icon] || Icons.Circle;
            const isOpen = active === i;
            return (
              <button
                key={c.title}
                onClick={() => setActive(isOpen ? null : i)}
                data-testid={`circle-card-${i}`}
                className={`group text-left relative hfa-orange-surface border rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 ${
                  isOpen
                    ? "border-orange-600 shadow-[0_20px_50px_-20px_rgba(234,88,12,0.25)]"
                    : "border-orange-600/20 hover:border-orange-600/45"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                      isOpen ? "bg-orange-600 text-white" : "bg-black text-white group-hover:bg-orange-600"
                    }`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </div>
                  <span
                    className={`relative w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${
                      isOpen
                        ? "border-orange-600 bg-orange-600 text-white"
                        : "border-orange-600/25 bg-[#EBA875] text-[#7C2D12] group-hover:bg-orange-600 group-hover:border-orange-600 group-hover:text-white"
                    }`}
                    aria-hidden="true"
                  >
                    {!isOpen && (
                      <span
                        className="absolute inset-0 rounded-full pointer-events-none animate-circle-pulse"
                        style={{ animationDelay: `${(i % 9) * 0.55}s` }}
                      />
                    )}
                    <Plus
                      className={`w-3.5 h-3.5 transition-transform duration-300 ${isOpen ? "rotate-45" : "rotate-0"}`}
                    />
                  </span>
                </div>

                <h3 className="font-display mt-4 text-xl md:text-[22px] font-semibold tracking-tight">
                  {c.title}
                </h3>
                <p className="mt-1.5 text-sm text-black/90 leading-relaxed">
                  {c.fields.slice(0, 4).join(" · ")} and more
                </p>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                      data-testid={`circle-expanded-${i}`}
                    >
                      <div className="hairline my-5" />
                      <div className="flex flex-wrap gap-1.5">
                        {c.fields.map((f) => (
                          <span
                            key={f}
                            className="text-xs px-2.5 py-1.5 rounded-full bg-black/5 text-black/90 border border-black/5"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            );
          })}
        </div>

        {/* Highlight callout for non-listed fields */}
        <div
          data-testid="circles-callout"
          className="relative mt-8 md:mt-10 rounded-3xl bg-[#0A0A0A] text-white p-7 md:p-10 overflow-hidden"
        >
          <div
            aria-hidden="true"
            className="absolute -top-16 -right-12 w-72 h-72 rounded-full bg-orange-600/30 blur-3xl pointer-events-none"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-orange-600/15 blur-3xl pointer-events-none"
          />
          <div className="relative flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-orange-600 text-white flex items-center justify-center shadow-[0_18px_40px_-10px_rgba(234,88,12,0.8)]">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div
                data-testid="circles-callout-label"
                className="font-display font-bold text-orange-500 text-base sm:text-lg md:text-xl"
              >
                Is your field not listed here?
              </div>
              <h3
                data-testid="circles-callout-headline"
                className="font-display mt-4 text-xl md:text-2xl font-semibold tracking-tight leading-snug"
              >
                You still belong here.
              </h3>
              <p className="mt-5 text-base md:text-lg text-white/75 leading-relaxed max-w-3xl" data-testid="callout-p1">
                Every member becomes part of their City Chapter and its City
                Members Group, connecting with professionals across industries.
                Professional Circles are an additional layer for connecting
                more closely with people in your field.
              </p>
              <p className="mt-4 text-base md:text-lg text-white/75 leading-relaxed max-w-3xl" data-testid="callout-p2">
                You don&rsquo;t need to belong to one of the listed Professional
                Circles to be part of the community.
              </p>
              <p className="mt-4 text-base md:text-lg text-white/75 leading-relaxed max-w-3xl" data-testid="callout-p3">
                Whether you belong to a Professional Circle or your field
                isn&rsquo;t listed, you&rsquo;re still part of the same City
                Chapter and can connect, collaborate, seek referrals, and share
                your professional requirements across the community.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
